/* eslint-disable camelcase -- Firestore field names are the persisted Turkish schema. */
// LG-68 "Tenis Yılın": oyuncunun bir yıldaki onaylı tekler maçlarından kişisel özet. Saf hesap;
// veri okuma `lib/yearReview.ts`te. Yıl sınırı İstanbul saatine göredir (UTC+3).

const ISTANBUL_OFFSET_MS = 3 * 60 * 60 * 1000;

export type ReviewMatch = {
  id: string;
  at: number;
  leagueId: string;
  opponentId: string;
  won: boolean;
  sets: { mine: number; theirs: number }[];
};

export type YearReview = {
  year: number;
  matches: number;
  wins: number;
  losses: number;
  setsWon: number;
  setsLost: number;
  gamesWon: number;
  gamesLost: number;
  longestStreak: number;
  revengeWins: number;
  leagueIds: string[];
  topOpponent: { id: string; matches: number; wins: number; losses: number } | null;
  busiestMonth: { month: number; matches: number } | null;
  first: { at: number; opponentId: string; won: boolean } | null;
  last: { at: number; opponentId: string; won: boolean } | null;
};

/** Yılın İstanbul'daki başlangıç ve bitişi (ms, bitiş hariç). */
export const yearRange = (year: number) => ({
  start: Date.UTC(year, 0, 1) - ISTANBUL_OFFSET_MS,
  end: Date.UTC(year + 1, 0, 1) - ISTANBUL_OFFSET_MS,
});

const istanbulMonth = (at: number) => new Date(at + ISTANBUL_OFFSET_MS).getUTCMonth() + 1;

const toMillis = (value: any): number => {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  return NaN;
};

/**
 * Ham `mac_durum_sonuc` belgesini oyuncunun bakışına çevirir. Onaysız, kazananı belli olmayan,
 * zamanı okunamayan ya da oyuncunun olmadığı maç null.
 */
export function toReviewMatch(id: string, data: any, playerId: string): ReviewMatch | null {
  if (!data || data.mac_durum !== 2) return null;
  const side = data.kul_t_id === playerId ? 't' : data.kul_r_id === playerId ? 'r' : null;
  if (!side) return null;
  const other = side === 't' ? 'r' : 't';
  const opponentId = data[`kul_${other}_id`];
  const mine = Number(data[`kul_${side}_sonuc`]) || 0;
  const theirs = Number(data[`kul_${other}_sonuc`]) || 0;
  const at = toMillis(data.mac_saati);
  if (typeof opponentId !== 'string' || !opponentId || mine === theirs || !Number.isFinite(at)) return null;
  const sets = [1, 2, 3]
    .map(index => ({
      mine: Number(data[`kul_${side}_sonuc_set${index}`]) || 0,
      theirs: Number(data[`kul_${other}_sonuc_set${index}`]) || 0,
    }))
    .filter(set => set.mine > 0 || set.theirs > 0);
  return {
    id,
    at,
    leagueId: typeof data.lig_temel_id === 'string' ? data.lig_temel_id : '',
    opponentId,
    won: mine > theirs,
    sets,
  };
}

export function computeYearReview(all: ReviewMatch[], year: number): YearReview {
  const { start, end } = yearRange(year);
  const matches = all
    .filter(match => match.at >= start && match.at < end)
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
  let streak = 0;
  let longestStreak = 0;
  let revengeWins = 0;
  const lastResult = new Map<string, boolean>();
  const opponents = new Map<string, { matches: number; wins: number; losses: number; lastAt: number }>();
  const months = new Map<number, number>();
  const review: YearReview = {
    year,
    matches: matches.length,
    wins: 0,
    losses: 0,
    setsWon: 0,
    setsLost: 0,
    gamesWon: 0,
    gamesLost: 0,
    longestStreak: 0,
    revengeWins: 0,
    leagueIds: [],
    topOpponent: null,
    busiestMonth: null,
    first: null,
    last: null,
  };
  const leagues = new Set<string>();
  matches.forEach(match => {
    if (match.won) review.wins += 1;
    else review.losses += 1;
    match.sets.forEach(set => {
      if (set.mine > set.theirs) review.setsWon += 1;
      else if (set.theirs > set.mine) review.setsLost += 1;
      review.gamesWon += set.mine;
      review.gamesLost += set.theirs;
    });
    streak = match.won ? streak + 1 : 0;
    longestStreak = Math.max(longestStreak, streak);
    // Rövanş: bu rakibe karşı bir önceki maçı kaybetmişti, bu maçı kazandı.
    if (match.won && lastResult.get(match.opponentId) === false) revengeWins += 1;
    lastResult.set(match.opponentId, match.won);
    const opponent = opponents.get(match.opponentId) || { matches: 0, wins: 0, losses: 0, lastAt: 0 };
    opponent.matches += 1;
    if (match.won) opponent.wins += 1;
    else opponent.losses += 1;
    opponent.lastAt = match.at;
    opponents.set(match.opponentId, opponent);
    const month = istanbulMonth(match.at);
    months.set(month, (months.get(month) || 0) + 1);
    if (match.leagueId) leagues.add(match.leagueId);
  });
  review.longestStreak = longestStreak;
  review.revengeWins = revengeWins;
  review.leagueIds = Array.from(leagues);
  // En çok karşılaşılan rakip; eşitlikte en son oynanan. Tek maçlık rakip "en çok" sayılmaz.
  const top = Array.from(opponents).sort(
    ([, a], [, b]) => b.matches - a.matches || b.lastAt - a.lastAt,
  )[0];
  if (top && top[1].matches >= 2) {
    review.topOpponent = { id: top[0], matches: top[1].matches, wins: top[1].wins, losses: top[1].losses };
  }
  const busiest = Array.from(months).sort(([a, x], [b, y]) => y - x || a - b)[0];
  if (busiest) review.busiestMonth = { month: busiest[0], matches: busiest[1] };
  const edge = (match?: ReviewMatch) =>
    match ? { at: match.at, opponentId: match.opponentId, won: match.won } : null;
  review.first = edge(matches[0]);
  review.last = edge(matches[matches.length - 1]);
  return review;
}

// Duyuru kartı 1 Aralık'tan Ocak sonuna kadar (İstanbul); sayfa adresle her zaman açılır.
export function isYearReviewSeason(now = Date.now()): { open: boolean; year: number } {
  const local = new Date(now + ISTANBUL_OFFSET_MS);
  const month = local.getUTCMonth() + 1;
  const year = local.getUTCFullYear();
  if (month === 12) return { open: true, year };
  if (month === 1) return { open: true, year: year - 1 };
  return { open: false, year };
}
